import { Trans } from '@lingui/react/macro'
import { Badge } from 'designSystem/components/Badge'
import { RevisionChangeCropPair } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionChangeCropPair'
import type { RevisionDetailEventRow } from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionDiffModel'
import styled from 'styled-components'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-left: 0.125rem solid var(--border);
  font-size: 0.8125rem;
`

const ClassifierRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`

const DetailsText = styled.p`
  margin: 0;
  color: var(--foreground);
  white-space: pre-wrap;
`

const TextPair = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.125rem 0.5rem;
  margin: 0;
`

const TextTerm = styled.dt`
  color: var(--muted-foreground);
  font-size: 0.75rem;
`

const TextValue = styled.dd`
  margin: 0;
  overflow-wrap: anywhere;
`

const EvidenceList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  margin: 0;
  padding: 0;
  color: var(--muted-foreground);
  font-size: 0.75rem;
  list-style: none;
`

type RevisionChangeCitationsProps = {
  event: RevisionDetailEventRow
  fromImageUrl: string | undefined
  toImageUrl: string | undefined
}

/**
 * Evidence for one change. Backend-authored text and classifier values are
 * data, not UI copy, so they render verbatim and are never localized.
 */
export function RevisionChangeCitations({
  event,
  fromImageUrl,
  toImageUrl,
}: RevisionChangeCitationsProps) {
  const classifiers = [
    event.kind,
    event.impact,
    event.category,
    event.action,
    event.tier,
    event.confidence,
  ].filter((value): value is string => value !== null)
  const evidence = event.pixelEvidence
    ? Object.entries(event.pixelEvidence)
    : []

  return (
    <Root>
      {classifiers.length > 0 ? (
        <ClassifierRow>
          {classifiers.map((value) => (
            <Badge key={value} variant='outlineMuted'>
              {value}
            </Badge>
          ))}
        </ClassifierRow>
      ) : null}
      <DetailsText>{event.details}</DetailsText>
      {event.oldText !== null || event.newText !== null ? (
        <TextPair>
          {event.oldText !== null ? (
            <>
              <TextTerm>
                <Trans>Before</Trans>
              </TextTerm>
              <TextValue>{event.oldText}</TextValue>
            </>
          ) : null}
          {event.newText !== null ? (
            <>
              <TextTerm>
                <Trans>After</Trans>
              </TextTerm>
              <TextValue>{event.newText}</TextValue>
            </>
          ) : null}
        </TextPair>
      ) : null}
      {event.cloudState !== null ? (
        <TextPair>
          <TextTerm>
            <Trans>Revision cloud</Trans>
          </TextTerm>
          <TextValue>{event.cloudState}</TextValue>
        </TextPair>
      ) : null}
      {evidence.length > 0 ? (
        <EvidenceList>
          {evidence.map(([key, value]) => (
            <li key={key}>
              {key}: {String(value)}
            </li>
          ))}
        </EvidenceList>
      ) : null}
      <RevisionChangeCropPair
        status={event.status}
        fromImageUrl={fromImageUrl}
        toImageUrl={toImageUrl}
        fromBox={event.fromBox}
        toBox={event.toBox}
      />
    </Root>
  )
}
